import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { Icon } from '@iconify/react';
import { NavLink as RouterLink, matchPath, useLocation } from 'react-router-dom';
import arrowIosForwardFill from '@iconify/icons-eva/arrow-ios-forward-fill';
import arrowIosDownwardFill from '@iconify/icons-eva/arrow-ios-downward-fill';
// material
import { alpha, useTheme, styled } from '@mui/material/styles';
import {
  Box,
  List,
  Collapse,
  ListItemText,
  ListItemIcon,
  ListSubheader,
  ListItemButton,
} from '@mui/material';
import useAuth from '../hooks/useAuth';
import { PARENT_ROLE } from '../constants/roles';
import { defaultsConfig } from '../config';

// ----------------------------------------------------------------------

const ListSubheaderStyle = styled((props) => (
  <ListSubheader disableSticky disableGutters {...props} />
))(({ theme }) => ({
  ...theme.typography.overline,
  marginTop: theme.spacing(3),
  marginBottom: theme.spacing(2),
  paddingLeft: theme.spacing(5),
  color: theme.palette.text.primary,
}));

const ListItemStyle = styled(ListItemButton)(({ theme }) => ({
  ...theme.typography.body2,
  height: 48,
  position: 'relative',
  textTransform: 'capitalize',
  paddingLeft: theme.spacing(5),
  paddingRight: theme.spacing(2.5),
  color: theme.palette.text.secondary,
  '&:before': {
    top: 0,
    right: 0,
    width: 3,
    bottom: 0,
    content: "''",
    display: 'none',
    position: 'absolute',
    borderTopLeftRadius: 4,
    borderBottomLeftRadius: 4,
    backgroundColor: theme.palette.primary.main,
  },
}));

const ListItemIconStyle = styled(ListItemIcon)({
  width: 22,
  height: 22,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

// ----------------------------------------------------------------------

NavItem.propTypes = {
  isShow: PropTypes.bool,
  item: PropTypes.object,
};

function NavItem({ item, isShow }) {
  const theme = useTheme();
  const { pathname } = useLocation();
  const { title, path, icon, info, children } = item;
  const { user } = useAuth();

  const isActiveRoot = path ? !!matchPath({ path, end: false }, pathname) : false;

  const [open, setOpen] = useState(isActiveRoot);

  const handleOpen = () => {
    setOpen(!open);
  };

  const activeRootStyle = {
    color: 'primary.main',
    fontWeight: 'fontWeightMedium',
    bgcolor: alpha(theme.palette.primary.main, theme.palette.action.selectedOpacity),
    '&:before': { display: 'block' },
  };

  const activeSubStyle = {
    color: 'text.primary',
    fontWeight: 'fontWeightMedium',
  };

  if (children) {
    return (
      <>
        <ListItemStyle
          data-tour={`nav-${title}`}
          onClick={handleOpen}
          sx={{
            ...(isActiveRoot && activeRootStyle),
          }}
        >
          <ListItemIconStyle>{icon && icon}</ListItemIconStyle>

          {isShow && (
            <>
              <ListItemText disableTypography primary={title} />
              {info && info}
              <Box
                component={Icon}
                icon={open ? arrowIosDownwardFill : arrowIosForwardFill}
                sx={{ width: 16, height: 16, ml: 1 }}
              />
            </>
          )}
        </ListItemStyle>

        {isShow && (
          <Collapse in={open} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {children.map((item) => {
                const { title, path } = item;

                let rPath = path;

                if (typeof path === 'function') {
                  rPath = path(user);
                }

                const isActiveSub = rPath
                  ? !!matchPath({ path: rPath, end: false }, pathname)
                  : false;

                // Hide "company" under Settings for guest users
                if (
                  title === 'company' &&
                  user.company_id === parseInt(defaultsConfig.guestCompanyId, 10)
                ) {
                  return null;
                }

                return (
                  <ListItemStyle
                    key={title}
                    component={RouterLink}
                    to={rPath}
                    sx={{
                      ...(isActiveSub && activeSubStyle),
                    }}
                  >
                    <ListItemIconStyle>
                      <Box
                        component="span"
                        sx={{
                          width: 4,
                          height: 4,
                          display: 'flex',
                          borderRadius: '50%',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: 'text.disabled',
                          transition: (theme) => theme.transitions.create('transform'),
                          ...(isActiveSub && {
                            transform: 'scale(2)',
                            bgcolor: 'primary.main',
                          }),
                        }}
                      />
                    </ListItemIconStyle>
                    <ListItemText disableTypography primary={title} />
                  </ListItemStyle>
                );
              })}
            </List>
          </Collapse>
        )}
      </>
    );
  }

  return (
    <ListItemStyle
      data-tour={`nav-${title}`}
      component={RouterLink}
      to={path}
      sx={{
        ...(isActiveRoot && activeRootStyle),
      }}
    >
      <ListItemIconStyle>{icon && icon}</ListItemIconStyle>
      {isShow && (
        <>
          <ListItemText disableTypography primary={title} />
          {info && info}
        </>
      )}
    </ListItemStyle>
  );
}

NavSection.propTypes = {
  isShow: PropTypes.bool,
  navConfig: PropTypes.array,
};

export default function NavSection({ navConfig, isShow = true, ...other }) {
  const { firebaseUser, user } = useAuth();
  const [firebaseRole, setFirebaseRole] = useState('user');
  // const [showMenu, setShowMenu] = useState(false);

  useEffect(() => {
    if (firebaseUser && firebaseUser.customClaims) {
      const role =
        firebaseUser.customClaims['https://hasura.io/jwt/claims']['x-hasura-default-role'];
      setFirebaseRole(role);
    }
  }, [firebaseUser]);

  if (!user.company_id) {
    return null;
  }

  return (
    <Box {...other}>
      {navConfig.map((list) => {
        const { subheader, items } = list;
        return (
          <List key={subheader} disablePadding>
            {isShow && <ListSubheaderStyle>{subheader}</ListSubheaderStyle>}
            {items.map((item, index) => {
              // Hide certain items for guest users
              if (
                user.company_id === parseInt(defaultsConfig.guestCompanyId, 10) &&
                (item.title === 'Products' ||
                  item.title === 'Companies' ||
                  item.title === 'Users' ||
                  item.title === 'Earlymarket' ||
                  (list.subheader === 'Settings' && item.title === 'Company'))
              ) {
                return null;
              }
              // ...existing conditions...
              if (
                (item.title === 'Companies' && user.company_type === 'architect') ||
                (item.title === 'Earlymarket' && import.meta.env.VITE_APP_ENV_NAME === 'prod') ||
                (user.company_type === 'manufacturer' && item.title === 'Architect Dashboard') ||
                (user.company_type !== 'manufacturer' && item.title === 'Manufacturer Dashboard') ||
                (user.company_type === 'manufacturer' &&
                  !user.company_plan &&
                  (item.title === 'Projects' ||
                    item.title === 'Schedules' ||
                    item.title === 'Companies' ||
                    item.title === 'calendar' ||
                    item.title === 'Earlymarket')) ||
                (item.title === 'Analytics' &&
                  (PARENT_ROLE[firebaseRole] !== 'partner' ||
                    user.company_id !== parseInt(defaultsConfig.bossConsultancyid, 10))) ||
                (item.title === 'Admin' &&
                  (PARENT_ROLE[firebaseRole] !== 'partner' ||
                    user.company_id !== parseInt(defaultsConfig.bossConsultancyid, 10))) ||
                (item.title === 'company' && user.company_role !== 'admin')
              ) {
                return null;
              }
              return <NavItem key={item.title} item={item} isShow={isShow} />;
            })}
          </List>
        );
      })}
    </Box>
  );
}
